<template>
    <div class="view-container">
        <div v-if="!standalone && item && item.token" class="mb-4 flex justify-end">
            <a :href="getViewInNewTabUrl()" target="_blank" rel="noopener noreferrer"
                class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-ternary hover:bg-ternary/5 rounded-lg border border-ternary/30">
                <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                Open in new tab
            </a>
        </div>
        <!-- Basic Information Section -->
        <section class="view-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'building']" class="text-lg" />
                </div>
                <h2 class="section-title">Basic Information</h2>
            </div>

            <div class="info-grid">
                <div class="info-logo-card">
                    <div class="info-logo-wrapper">
                        <img class="info-logo-img" :src="getSrcFile(item.logo)" alt="Partner logo">
                    </div>
                </div>
                <div class="info-details">
                    <div class="info-row">
                        <span class="info-label">Name</span>
                        <span class="info-value">{{ item.name }}</span>
                    </div>
                    <div class="info-row">
                        <span class="info-label">Entity</span>
                        <span class="info-value">{{ item.entity }}</span>
                    </div>
                    <div class="info-row">
                        <span class="info-label">Industry</span>
                        <span class="info-value">{{ (item.industry_relation && item.industry_relation.name) ? item.industry_relation.name : '-' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="info-label">Type of Collaboration</span>
                        <span class="info-value">{{ item.type_of_collaboration || '—' }}</span>
                    </div>
                    <div class="info-row">
                        <span class="info-label">Classifications</span>
                        <span class="info-value">{{ formatClassifications(item) }}</span>
                    </div>
                    <div v-if="item.parent_partner" class="info-row">
                        <span class="info-label">Group/Mother Company</span>
                        <span class="info-value">{{ item.parent_partner.name }} ({{ item.parent_partner.is_pipeline ? 'Pipeline' : 'Current' }})</span>
                    </div>
                    <div class="info-row">
                        <span class="info-label">Status</span>
                        <span class="info-value">{{ item.status || '—' }}</span>
                    </div>
                    <div v-if="item.note" class="info-row">
                        <span class="info-label">Remarks</span>
                        <span class="info-value">{{ item.note }}</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- Child Partners Section -->
        <section v-if="hasChildPartners" class="view-section">
            <div class="section-header">
                <div class="section-icon section-icon-children">
                    <font-awesome-icon :icon="['fas', 'sitemap']" class="text-lg" />
                </div>
                <h2 class="section-title">Child Partners ({{ subsidiaryPartners.length }})</h2>
            </div>

            <div class="data-table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th class="data-table-th data-table-th-num">#</th>
                            <th class="data-table-th">Name</th>
                            <th class="data-table-th">Logo</th>
                            <th class="data-table-th">Entity</th>
                            <th class="data-table-th">Industry</th>
                            <th class="data-table-th">Partner Type</th>
                            <th class="data-table-th">Type of Collaboration</th>
                            <th class="data-table-th">Classifications</th>
                            <th class="data-table-th data-table-th-action">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(child, index) in subsidiaryPartners" :key="child.id" class="data-table-tr">
                            <td class="data-table-td data-table-td-num">{{ index + 1 }}</td>
                            <td class="data-table-td">{{ child.name }}</td>
                            <td class="data-table-td">
                                <img v-if="child.logo" class="h-10 w-10 rounded object-contain" :src="getSrcFile(child.logo)" alt="Logo" />
                                <span v-else class="text-slate-400">—</span>
                            </td>
                            <td class="data-table-td">{{ child.entity ? getEntityName(child.entity) : '—' }}</td>
                            <td class="data-table-td">{{ (child.industry_relation && child.industry_relation.name) ? child.industry_relation.name : '—' }}</td>
                            <td class="data-table-td">{{ child.is_pipeline ? 'Pipeline' : 'Current' }}</td>
                            <td class="data-table-td">{{ child.type_of_collaboration || '—' }}</td>
                            <td class="data-table-td">{{ formatClassifications(child) }}</td>
                            <td class="data-table-td data-table-td-action">
                                <button type="button" @click="viewChildPartner(child)"
                                    class="view-link view-link-btn">
                                    <icon-eye class="view-link-icon"></icon-eye>
                                    <span>View</span>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <!-- Contact Person Section -->
        <section class="view-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'users']" class="text-lg" />
                </div>
                <h2 class="section-title">Contact Person</h2>
            </div>

            <div v-if="hasContactPerson" class="data-table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th class="data-table-th data-table-th-num">#</th>
                            <th class="data-table-th">Name</th>
                            <th class="data-table-th">Phone</th>
                            <th class="data-table-th">Email</th>
                            <th class="data-table-th">Designation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(cp, index) in item.contact_person" :key="index" class="data-table-tr">
                            <td class="data-table-td data-table-td-num">{{ index + 1 }}</td>
                            <td class="data-table-td">{{ cp.name }}</td>
                            <td class="data-table-td">{{ cp.phone }}</td>
                            <td class="data-table-td">
                                <a :href="'mailto:' + cp.email" class="data-table-link">{{ cp.email }}</a>
                            </td>
                            <td class="data-table-td">{{ cp.designation }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="empty-state">
                <font-awesome-icon :icon="['fas', 'user-slash']" class="empty-state-icon" />
                <p class="empty-state-text">No contact information available</p>
            </div>
        </section>

        <!-- Uploaded Documents Section -->
        <section class="view-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'paperclip']" class="text-lg" />
                </div>
                <h2 class="section-title">Uploaded Documents</h2>
            </div>

            <div v-if="hasUploadedDocuments" class="data-table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th class="data-table-th data-table-th-num">#</th>
                            <th class="data-table-th">Document Name</th>
                            <th class="data-table-th">File Name</th>
                            <th class="data-table-th data-table-th-action">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(attachment, index) in uploadedDocuments" :key="index" class="data-table-tr">
                            <td class="data-table-td data-table-td-num">{{ index + 1 }}</td>
                            <td class="data-table-td">{{ attachment.file_name }}</td>
                            <td class="data-table-td">{{ attachment.original_filename }}</td>
                            <td class="data-table-td data-table-td-action">
                                <div class="action-buttons">
                                    <button type="button" @click="openDocumentPreview(attachment)"
                                        class="view-link view-link-btn">
                                        <icon-eye class="view-link-icon"></icon-eye>
                                        <span>View</span>
                                    </button>
                                    <button type="button" @click="downloadAttachment(attachment)"
                                        class="view-link view-link-btn download-link-btn">
                                        <font-awesome-icon :icon="['fas', 'download']" class="view-link-icon"></font-awesome-icon>
                                        <span>Download</span>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="empty-state">
                <font-awesome-icon :icon="['fas', 'folder-open']" class="empty-state-icon" />
                <p class="empty-state-text">No documents uploaded</p>
            </div>
        </section>

        <!-- Management Approval Documents Section -->
        <section v-if="isShowManagementApprovalDocuments" class="view-section">
            <div class="section-header">
                <div class="section-icon section-icon-approval">
                    <font-awesome-icon :icon="['fas', 'stamp']" class="text-lg" />
                </div>
                <h2 class="section-title">Management Approval Documents</h2>
            </div>

            <div v-if="hasManagementApprovalDocuments" class="data-table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th class="data-table-th data-table-th-num">#</th>
                            <th class="data-table-th">Document Name</th>
                            <th class="data-table-th">File Name</th>
                            <th class="data-table-th data-table-th-action">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(attachment, index) in managementApprovalDocuments" :key="index"
                            class="data-table-tr">
                            <td class="data-table-td data-table-td-num">{{ index + 1 }}</td>
                            <td class="data-table-td">{{ attachment.file_name }}</td>
                            <td class="data-table-td">{{ attachment.original_filename }}</td>
                            <td class="data-table-td data-table-td-action">
                                <div class="action-buttons">
                                    <button type="button" @click="openDocumentPreview(attachment)"
                                        class="view-link view-link-btn">
                                        <icon-eye class="view-link-icon"></icon-eye>
                                        <span>View</span>
                                    </button>
                                    <button type="button" @click="downloadAttachment(attachment)"
                                        class="view-link view-link-btn download-link-btn">
                                        <font-awesome-icon :icon="['fas', 'download']" class="view-link-icon"></font-awesome-icon>
                                        <span>Download</span>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="empty-state">
                <font-awesome-icon :icon="['fas', 'folder-open']" class="empty-state-icon" />
                <p class="empty-state-text">No documents uploaded</p>
            </div>
        </section>

        <!-- Document Preview Modal -->
        <base-modal v-if="showDocumentPreviewModal" :title="previewModalTitle" @close="closeDocumentPreview"
            width="90vw" height="85vh">
            <div class="document-preview-container">
                <div v-if="previewAttachment" class="document-preview-content">
                    <!-- PDF Preview -->
                    <div v-if="isPdfFile(previewAttachment)" class="document-preview-iframe">
                        <iframe :src="getSrcFile(previewAttachment.path)" class="document-iframe"
                            frameborder="0"></iframe>
                    </div>
                    <!-- Image Preview -->
                    <div v-else-if="isImageFile(previewAttachment)" class="document-preview-image">
                        <img :src="getSrcFile(previewAttachment.path)" :alt="previewAttachment.file_name"
                            class="document-img" />
                    </div>
                    <!-- Fallback for other file types -->
                    <div v-else class="document-preview-fallback">
                        <font-awesome-icon :icon="['fas', 'file-alt']" class="document-fallback-icon" />
                        <p class="document-fallback-text">Preview not available for this file type.</p>
                        <p class="document-fallback-hint">Please download to view the content.</p>
                    </div>
                </div>
                <div v-if="previewAttachment" class="document-preview-footer">
                    <button type="button" @click="downloadAttachment(previewAttachment)" class="document-download-btn">
                        <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                        Download
                    </button>
                </div>
            </div>
        </base-modal>
    </div>
</template>

<script>
import gql from "graphql-tag";
import IconEye from "../../components/icons/IconEye.vue";
import BaseModal from "../../components/ui/BaseModal.vue";
export default {
    name: "pipeline-partners-view",
    components: {
        IconEye,
        BaseModal,
    },
    props: {
        item: { type: Object, required: true },
        standalone: { type: Boolean, default: false },
    },
    data() {
        return {
            showDocumentPreviewModal: false,
            previewAttachment: null,
        };
    },
    computed: {
        hasContactPerson() {
            return this.item.contact_person && this.item.contact_person.length > 0;
        },
        hasUploadedDocuments() {
            return Array.isArray(this.item.attachments) &&
                this.item.attachments.some(att => att.uploaded_from === 'Upload Documents');
        },
        uploadedDocuments() {
            if (this.hasUploadedDocuments) {
                return this.item.attachments.filter(att => att.uploaded_from === 'Upload Documents')
            }
            return [];
        },
        hasManagementApprovalDocuments() {
            return Array.isArray(this.item.attachments) &&
                this.item.attachments.some(att => att.uploaded_from === 'Management Approval');
        },
        managementApprovalDocuments() {
            if (this.hasManagementApprovalDocuments) {
                return this.item.attachments.filter(att => att.uploaded_from === 'Management Approval')
            }
            return [];
        },
        isShowManagementApprovalDocuments() {
            const { is_bot_approval, is_md_approval, is_line_manager_approval } = this.item;

            return (
                is_bot_approval !== null ||
                is_md_approval !== null ||
                is_line_manager_approval !== null
            );
        },
        previewModalTitle() {
            return this.previewAttachment
                ? "Document: " + (this.previewAttachment.file_name || this.previewAttachment.original_filename)
                : "Document Preview";
        },
        hasChildPartners() {
            return this.item.subsidiary_partners && this.item.subsidiary_partners.length > 0;
        },
        subsidiaryPartners() {
            return this.item.subsidiary_partners || [];
        },
    },
    methods: {
        getSrcFile(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        getEntityName(entity) {
            return entity.replace(/ATHS/g, "ATS");
        },
        formatClassifications(row) {
            if (!row || !row.classifications || row.classifications.length === 0) {
                return "—";
            }
            return row.classifications.join(", ");
        },
        getViewInNewTabUrl() {
            const route = this.$router.resolve({
                name: "Pipeline Partner View",
                params: { token: this.item.token },
            });
            return route.href;
        },
        viewChildPartner(child) {
            const routeName = child.is_pipeline ? "Pipeline Partner View" : "Current Partner View";
            const route = this.$router.resolve({
                name: routeName,
                params: { token: child.token },
            });
            window.open(route.href, "_blank");
        },
        openDocumentPreview(attachment) {
            this.previewAttachment = attachment;
            this.showDocumentPreviewModal = true;
        },
        closeDocumentPreview() {
            this.showDocumentPreviewModal = false;
            this.previewAttachment = null;
        },
        isPdfFile(attachment) {
            if (!attachment || !attachment.file_extension) return false;
            const ext = attachment.file_extension.toLowerCase();
            return ext === "pdf";
        },
        isImageFile(attachment) {
            if (!attachment || !attachment.file_extension) return false;
            const ext = attachment.file_extension.toLowerCase();
            return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].indexOf(ext) >= 0;
        },
        async forceDownload(attachmentId, filename) {
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation GetCurrentPartnerAttachmentContent($id: ID!) {
                            getCurrentPartnerAttachmentContent(id: $id) {
                                error
                                error_code
                                message
                                data {
                                    content
                                    file_type
                                }
                            }
                        }
                    `,
                    variables: {
                        id: attachmentId
                    }
                });

                if (response.data.getCurrentPartnerAttachmentContent.error) {
                    throw new Error(response.data.getCurrentPartnerAttachmentContent.message);
                }

                const base64Content = response.data.getCurrentPartnerAttachmentContent.data.content;
                const fileType = response.data.getCurrentPartnerAttachmentContent.data.file_type;
                const binaryString = atob(base64Content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const blob = new Blob([bytes], { type: fileType });
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = filename;
                link.style.display = "none";

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                window.URL.revokeObjectURL(blobUrl);
            } catch (error) {
                console.error("Error downloading file:", error);
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "error",
                        title: "Download Failed",
                        text: "Failed to download the file. Please try again."
                    });
                }
            }
        },
        async downloadAttachment(attachment) {
            try {
                await this.forceDownload(attachment.id, attachment.original_filename);
            } catch (error) {
                console.error("Error downloading attachment:", error);
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "error",
                        title: "Download Failed",
                        text: "Failed to download the file. Please try again."
                    });
                }
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.view-container {
    max-width: 56rem;
    margin: 0 auto;
    padding: 1.5rem 1.75rem;
}

.view-section {
    margin-bottom: 2rem;
    padding: 1.5rem 1.75rem;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
}

.section-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #f1f5f9;
}

.section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.5rem;
    background: linear-gradient(135deg, #212a3e 0%, #394867 100%);
    color: #fff;
}

.section-icon-approval {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.section-icon-children {
    background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
}

.section-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e293b;
    letter-spacing: -0.01em;
}

/* Basic Info Grid */
.info-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

@media (min-width: 640px) {
    .info-grid {
        flex-direction: row;
        align-items: flex-start;
    }
}

.info-logo-card {
    flex-shrink: 0;
}

.info-logo-wrapper {
    width: 8rem;
    height: 8rem;
    padding: 0.5rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
}

.info-logo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.info-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.info-row {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

@media (min-width: 640px) {
    .info-row {
        flex-direction: row;
        align-items: baseline;
        gap: 1rem;
    }
}

.info-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #64748b;
    min-width: 10rem;
}

.info-value {
    font-size: 0.9375rem;
    color: #1e293b;
}

/* Data Tables */
.data-table-wrapper {
    overflow-x: auto;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
}

.data-table-th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-weight: 600;
    color: #64748b;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.data-table-th-num {
    width: 3rem;
    text-align: center;
}

.data-table-th-action {
    text-align: center;
    width: 10rem;
}

.action-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    flex-wrap: nowrap;
    white-space: nowrap;
}

.download-link-btn {
    background: #059669;
    color: #fff;
    cursor: pointer;
}

.download-link-btn:hover {
    background: #047857;
    color: #fff;
}

.data-table-tr {
    transition: background 0.15s ease;
}

.data-table-tr:hover {
    background: #f8fafc;
}

.data-table-td {
    padding: 0.875rem 1rem;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
}

.data-table-td-num {
    text-align: center;
    font-weight: 500;
    color: #64748b;
}

.data-table-td-action {
    text-align: center;
}

.data-table-link {
    color: #0059ac;
    text-decoration: none;
}

.data-table-link:hover {
    text-decoration: underline;
}

/* View Link Button */
.view-link {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    flex-shrink: 0;
    white-space: nowrap;
    cursor: pointer;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #0059ac;
    text-decoration: none;
    background: #eff6ff;
    transition: all 0.2s ease;
}

.view-link:hover {
    background: #dbeafe;
    color: #1d4ed8;
}

.view-link-btn {
    border: none;
    font-family: inherit;
}

.view-link-icon {
    width: 1rem;
    height: 1rem;
}

/* Empty State */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px dashed #cbd5e1;
}

.empty-state-icon {
    font-size: 2.5rem;
    color: #94a3b8;
    margin-bottom: 0.75rem;
}

.empty-state-text {
    font-size: 0.9375rem;
    color: #64748b;
}

/* Document Preview Modal */
.document-preview-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 400px;
}

.document-preview-content {
    flex: 1;
    overflow: auto;
    min-height: 0;
}

.document-preview-iframe,
.document-preview-image {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
}

.document-iframe {
    width: 100%;
    height: 70vh;
    min-height: 400px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.document-img {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.document-preview-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
}

.document-fallback-icon {
    font-size: 4rem;
    color: #94a3b8;
    margin-bottom: 1rem;
}

.document-fallback-text {
    font-size: 1rem;
    font-weight: 500;
    color: #334155;
    margin-bottom: 0.5rem;
}

.document-fallback-hint {
    font-size: 0.875rem;
    color: #64748b;
    margin-bottom: 1.5rem;
}

.document-preview-footer {
    flex-shrink: 0;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: center;
}

.document-download-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #fff;
    background: #0066cc;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s ease;
}

.document-download-btn:hover {
    background: #0052a3;
    color: #fff;
}
</style>
